import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Checkbox from "../../../components/common/Checkbox";
import Dropdown from "../../../components/common/Dropdown";
import Badge from "../../../components/common/Badge";
import {
  MODULE_LABELS,
  PERMISSION_ACTION_LABELS,
  DEFAULT_PERMISSIONS,
} from "../../../constants/admin.constants";
import {
  updateAdminPermissionsThunk,
  applyPresetRoleThunk,
} from "../../../store/thunks/permissionThunks";
import {
  ShieldCheckIcon,
  LockOpenIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const PermissionModal = ({ admin, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { savingPermissions } = useSelector(
    (state) => state.adminPermission || state.permission || {}
  );

  const [permissions, setPermissions] = useState({});
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  // Deep clone helper function
  const deepClone = (obj) => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj);
    if (obj instanceof Array) return obj.map((item) => deepClone(item));

    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  };

  // Initialize permissions when admin changes
  useEffect(() => {
    if (admin && admin.accessControl) {
      // Create a deep copy of the accessControl object
      const clonedPermissions = deepClone(admin.accessControl);
      setPermissions(clonedPermissions || {});

      const preset = admin.rolePreset || "custom";
      setSelectedPreset({
        _id: preset,
        name: preset.charAt(0).toUpperCase() + preset.slice(1),
      });
    }
  }, [admin]);

  // Dynamically determine which modules to show based on role
  const getAvailableModules = () => {
    const roleModuleMap = {
      admin: Object.keys(MODULE_LABELS),
      branch_manager: ["products", "customers", "reports", "offers"],
      rate_manager: ["rates", "pricingRules"],
      staff: ["products", "customers"],
    };

    return roleModuleMap[admin?.role] || Object.keys(MODULE_LABELS);
  };

  // Dynamically determine available actions per module
  const getModuleActions = (moduleKey) => {
    const defaultActions = ["view", "create", "update", "delete"];
    const specialActions = {
      rates: [
        "view",
        "create",
        "update",
        "delete",
        "approve",
        "schedule",
        "export",
      ],
      reports: [
        "viewSales",
        "viewInventory",
        "viewFinancial",
        "viewCustomer",
        "export",
      ],
      products: [
        "view",
        "create",
        "update",
        "delete",
        "updatePricing",
        "manageStock",
        "bulkUpdate",
      ],
    };

    return specialActions[moduleKey] || defaultActions;
  };

  // Convert presets to Dropdown format
  const presetOptions = [
    ...Object.keys(DEFAULT_PERMISSIONS)
      .filter((preset) => preset !== "root")
      .map((preset) => ({
        _id: preset,
        name: preset.charAt(0).toUpperCase() + preset.slice(1),
      })),
    { _id: "custom", name: "Custom Permissions" },
  ];

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (
      preset?._id &&
      preset._id !== "custom" &&
      DEFAULT_PERMISSIONS[preset._id]
    ) {
      // Create a deep copy of the preset permissions
      const presetPermissions = deepClone(DEFAULT_PERMISSIONS[preset._id]);
      setPermissions(presetPermissions);
      setHasChanges(true);
    }
  };

  const handleModuleToggle = (module, enabled) => {
    setPermissions((prev) => {
      // Create a complete deep copy of the previous state
      const newPermissions = deepClone(prev);

      // Ensure the module exists in the permissions object
      if (!newPermissions[module]) {
        newPermissions[module] = {
          enabled: false,
          permissions: {},
          restrictions: {},
        };
      }

      // Now safely update the enabled property
      newPermissions[module].enabled = enabled;

      // Auto-enable basic permissions when enabling module
      if (
        enabled &&
        (!newPermissions[module].permissions ||
          Object.keys(newPermissions[module].permissions).length === 0)
      ) {
        const actions = getModuleActions(module);
        newPermissions[module].permissions = {};
        actions.forEach((action) => {
          newPermissions[module].permissions[action] = action === "view";
        });
      }

      return newPermissions;
    });
    setHasChanges(true);
  };

  const handlePermissionChange = (module, permission, value) => {
    setPermissions((prev) => {
      // Create a deep copy of the entire permissions object
      const newPermissions = deepClone(prev);

      // Ensure module structure exists
      if (!newPermissions[module]) {
        newPermissions[module] = {
          enabled: false,
          permissions: {},
          restrictions: {},
        };
      }
      if (!newPermissions[module].permissions) {
        newPermissions[module].permissions = {};
      }

      // Update the specific permission
      newPermissions[module].permissions[permission] = value;

      return newPermissions;
    });
    setHasChanges(true);
  };

  const toggleModuleExpansion = (module) => {
    setExpandedModules((prev) => ({
      ...prev,
      [module]: !prev[module],
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateAdminPermissionsThunk({
          adminId: admin._id,
          permissions,
        })
      ).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to update permissions:", error);
    }
  };

  const handleApplyPreset = async () => {
    if (!selectedPreset?._id) return;

    try {
      await dispatch(
        applyPresetRoleThunk({
          adminId: admin._id,
          rolePreset: selectedPreset._id,
        })
      ).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to apply preset:", error);
    }
  };

  const renderModuleCard = (moduleKey, moduleLabel) => {
    const moduleConfig = permissions[moduleKey] || {
      enabled: false,
      permissions: {},
    };
    const isExpanded = expandedModules[moduleKey];
    const moduleActions = getModuleActions(moduleKey);

    return (
      <div key={moduleKey} className="border rounded-lg overflow-hidden">
        {/* Module Header */}
        <div
          className={`p-3 cursor-pointer transition-colors ${
            moduleConfig.enabled ? "bg-blue-50" : "bg-gray-50"
          }`}
          onClick={() => toggleModuleExpansion(moduleKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={moduleConfig.enabled || false}
                  onChange={(e) =>
                    handleModuleToggle(moduleKey, e.target.checked)
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                {moduleConfig.enabled ? (
                  <LockOpenIcon className="w-4 h-4 text-blue-600" />
                ) : (
                  <LockClosedIcon className="w-4 h-4 text-gray-400" />
                )}
                <span
                  className={`font-medium ${
                    moduleConfig.enabled ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {moduleLabel}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {moduleConfig.enabled && (
                <Badge variant="success" className="text-xs">
                  Enabled
                </Badge>
              )}
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Module Permissions */}
        {isExpanded && moduleConfig.enabled && (
          <div className="p-4 bg-white border-t">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {moduleActions.map((action) => {
                const isEnabled = moduleConfig.permissions?.[action] || false;
                return (
                  <label
                    key={action}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={isEnabled}
                      onChange={(e) =>
                        handlePermissionChange(
                          moduleKey,
                          action,
                          e.target.checked
                        )
                      }
                    />
                    <span className="text-sm text-gray-700">
                      {PERMISSION_ACTION_LABELS[action] || action}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Module Restrictions */}
            {moduleConfig.restrictions &&
              Object.keys(moduleConfig.restrictions).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Restrictions
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(moduleConfig.restrictions).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm text-gray-600">
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                          </span>
                          <span className="text-sm font-medium">
                            {typeof value === "boolean" ? (
                              <Badge variant={value ? "success" : "default"}>
                                {value ? "Yes" : "No"}
                              </Badge>
                            ) : (
                              value
                            )}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    );
  };

  // Don't render if no admin
  if (!admin) return null;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title="Manage Permissions"
      className="sm:max-w-4xl"
      showConfirmationOnClose={hasChanges}
      confirmationMessage="You have unsaved changes. Are you sure you want to close?"
      hideCloseButton={true}
    >
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Admin Info Header */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{admin.name}</h3>
              <p className="text-sm text-gray-600">{admin.email}</p>
            </div>
            <Badge variant="info">{admin.role}</Badge>
          </div>
        </div>

        {/* Preset Selector */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Dropdown
              label="Permission Template"
              options={presetOptions}
              selected={selectedPreset}
              onChange={handlePresetChange}
              placeholder="Select a template"
              className="flex-1"
              clearable
            />
            {selectedPreset?._id !== "custom" && (
              <Button
                variant="secondary"
                onClick={handleApplyPreset}
                disabled={savingPermissions}
                className="mt-6"
              >
                Apply Template
              </Button>
            )}
          </div>
        </div>

        {/* Permissions Grid */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">
            Module Permissions
          </h4>
          {getAvailableModules().map((moduleKey) =>
            renderModuleCard(moduleKey, MODULE_LABELS[moduleKey])
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="sticky bottom-0  pt-4 mt-6 border-t">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {hasChanges ? (
              <span className="text-orange-600 font-medium">
                You have unsaved changes
              </span>
            ) : (
              "No changes made"
            )}
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={savingPermissions}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || savingPermissions}
            >
              {savingPermissions ? "Saving..." : "Save Permissions"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PermissionModal;
